import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import {
  Card, Descriptions, Tabs, Table, Tag, Typography, Statistic, Row, Col, Spinner,
  Space, Button, Tooltip, Progress, Avatar, theme, Result, Modal, Form, Input, DatePicker, EmptyState, message,
  Select, FilterBar, FilterResetButton, cn, type TagTone,
} from '../../ui';
import dayjs from 'dayjs';
import {
  Phone, MessageCircle, Mail, MapPin,
  ShoppingCart, FileText, Wrench, CalendarClock,
  Pencil, Store, FileLock, RefreshCw, ArrowLeftRight,
  Download, User, Percent, Paperclip, Smartphone, ShieldCheck, AlertTriangle, ExternalLink,
} from 'lucide-react';
import { FileUploadField } from '../../components/FileUploadField';
import { DocumentPreview } from '../../components/DocumentPreview';
import { CopyValue } from '../../components/CopyValue';
import { RegisteredBy } from '../../components/RegisteredBy';
import { LedgerStatement, LEDGER_TYPE_META, ledgerTypeLabel } from '../../components/LedgerStatement';
import { ExportDialog } from '../../components/ExportDialog';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { MAP_TILE_URL, MAP_TILE_ATTRIBUTION } from '../../lib/mapTiles';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { usePermission } from '../../store/auth';
import { useUrlTab } from '../../hooks/useUrlTab';
import { useUrlList, useUrlRangeOrNull } from '../../hooks/useUrlState';
import { disableFuture } from '../../lib/dateRange';
import { ChangeTerritoryModal } from '../territory/ChangeTerritoryModal';
import { TerritoryChips } from '../../components/TerritoryChips';
import { territoryOption } from '../../lib/territoryLabel';
import { displayMobile } from '../../lib/validators';
import { formatIst, formatIstDate, toIstDateString } from '../../lib/time';
import { inr2 } from '../../lib/format';
import { PALETTE, TAG_TONE_HUE } from '../../lib/palette';
import { retailerStatusLabel } from '../../lib/statusTone';
import { InlineEditField } from '../../components/InlineEditField';
import { STATE_OPTIONS, stateName } from '../../constants/indianStates';
import { inr as inrWhole } from '../../lib/format';

const STATUS_COLOR: Record<string, TagTone> = {
  ACTIVE: 'green', KYC_PENDING: 'gold', PROSPECT: 'blue', SUSPENDED: 'red', CLOSED: 'neutral',
};

/**
 * Section header — a tinted icon chip, the label, then a hairline running to the card edge.
 * The rule IS the separator, so sections no longer need a `border-t` + 24px of padding above
 * them as well; that double separation was most of the profile card's height.
 */
function SectionH({ icon, tint, children }: { icon: ReactNode; tint: string; children: ReactNode }) {
  return (
    <h4 className="mb-3 flex items-center gap-2">
      <span className={`flex size-6 shrink-0 items-center justify-center rounded-md ${tint}`}>{icon}</span>
      <span className="type-overline text-content">{children}</span>
      <span className="ml-1 h-px flex-1 bg-line/70" aria-hidden />
    </h4>
  );
}
/** One label/value pair — a small subtle label above a medium-weight value; muted em-dash when empty. */
function OField({ label, children, className }: { label: string; children?: ReactNode; className?: string }) {
  return (
    <div className={className}>
      <dt className="type-caption text-subtle">{label}</dt>
      <dd className="mt-1 type-body-strong text-content">{children ?? <span className="font-normal text-subtle">—</span>}</dd>
    </div>
  );
}
/**
 * The commercial schemes a dealer is enrolled in.
 *
 * Each one says whether it is actually IN FORCE — a scheme that has been superseded or whose dates
 * have passed is still part of the dealer's history, but listing it beside a live one without
 * saying so would tell the reader this dealer is on terms that lapsed last March. In force reads
 * green; everything else is muted and carries the reason.
 */
function SchemeEnrolments({ schemes, canView }: { schemes?: any[]; canView: boolean }) {
  const list = schemes ?? [];
  if (!list.length) return <span className="font-normal text-subtle">Not enrolled in any scheme</span>;

  const why = (x: any) => {
    if (x.assignmentStatus === 'REVOKED') return 'revoked';
    if (x.status === 'ARCHIVED') return 'archived';
    if (x.status === 'DRAFT') return 'draft';
    if (x.status === 'INACTIVE') return 'superseded';
    if (x.effectiveTo && x.effectiveTo < new Date().toISOString().slice(0, 10)) return 'ended';
    if (x.effectiveFrom && x.effectiveFrom > new Date().toISOString().slice(0, 10)) return 'not started';
    return 'inactive';
  };

  return (
    <div className="flex flex-wrap gap-1.5">
      {list.map((x) => {
        const label = (
          <span className="inline-flex items-center gap-1.5">
            <span className="truncate">{x.name}</span>
            <span className="opacity-70">FY {x.financialYear}</span>
            {x.version > 1 && <span className="opacity-70">v{x.version}</span>}
            {!x.inForce && <span className="opacity-70">· {why(x)}</span>}
          </span>
        );
        const tip = [
          x.inForce ? 'In force' : `Not in force — ${why(x)}`,
          x.effectiveFrom ? `From ${formatIstDate(x.effectiveFrom)}${x.effectiveTo ? ` to ${formatIstDate(x.effectiveTo)}` : ' (open-ended)'}` : null,
          x.creditPeriodDays ? `${x.creditPeriodDays}-day credit period` : null,
          // Only what the terms ARE. Acceptance is signed on paper and has no dealer-facing step,
          // so it is not reported here.
        ].filter(Boolean).join(' · ');

        const chip = (
          <Tag tone={x.inForce ? 'green' : 'neutral'}>{label}</Tag>
        );
        return (
          <Tooltip key={x.id} label={tip}>
            {canView
              // The scheme register is the only place these terms can be read in full; link there
              // only for someone who is allowed to open it.
              ? <Link to="/masters/commercial-terms" className="max-w-full">{chip}</Link>
              : <span className="max-w-full">{chip}</span>}
          </Tooltip>
        );
      })}
    </div>
  );
}

/** A monospace "code chip" for identifiers (GSTIN / PAN / coordinates) — subtle bordered pill so they stand out. */
function Mono({ children }: { children: ReactNode }) {
  return <span className="inline-block rounded-md border border-line bg-surface-2 px-2 py-1 font-mono text-[12.5px] leading-none tracking-tight text-content">{children}</span>;
}

const ORDER_COLOR: Record<string, TagTone> = {
  DRAFT: 'neutral', PLACED: 'blue', APPROVED: 'cyan', PACKED: 'gold', DISPATCHED: 'gold',
  DELIVERED: 'green', INVOICED: 'purple', CANCELLED: 'red', CLOSED: 'neutral',
};

const INVOICE_COLOR: Record<string, TagTone> = {
  OPEN: 'blue', PARTIAL: 'gold', PAID: 'green', OVERDUE: 'red', CANCELLED: 'neutral',
};

// The shared formatter: a negative reads "-₹480", never "₹-480".
const inr = inrWhole;


// Custom green pin for the embedded map
const retailerPin = L.divIcon({
  html: `<div style="transform:translate(-50%,-100%);">
    <svg viewBox="0 0 24 32" width="32" height="32">
      <path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20C24 5.4 18.6 0 12 0z"
            fill={PALETTE.brand[600]} stroke="white" stroke-width="1.5"/>
      <circle cx="12" cy="12" r="5" fill="white"/>
    </svg></div>`,
  className: '', iconSize: [32, 32], iconAnchor: [16, 32],
});

/**
 * The map sits inside AntD Tabs → Card → Col, so Leaflet initialises before that layout finishes sizing the
 * container — tiles get requested for the wrong size and never repaint (grey map with only the marker/controls).
 * Force a size recompute once the layout settles, and again on resize.
 */
function InvalidateSize() {
  const map = useMap();
  useEffect(() => {
    const fix = () => map.invalidateSize();
    const t0 = setTimeout(fix, 0);
    const t1 = setTimeout(fix, 300);
    window.addEventListener('resize', fix);
    return () => { clearTimeout(t0); clearTimeout(t1); window.removeEventListener('resize', fix); };
  }, [map]);
  return null;
}

/**
 * One segment of the money band. Four separate KPI cards cost ~150px of page height and left three
 * of them almost empty; a single banded card reads as one credit position and costs ~90px.
 * Borders are drawn per-segment so the band reflows 4→2 columns without stray dividers.
 */
function MoneyCell({ label, value, hint, tone }: { label: string; value: ReactNode; hint?: ReactNode; tone?: string }) {
  return (
    <div className="border-line px-4 py-3 [&:nth-child(n+3)]:border-t [&:nth-child(even)]:border-l md:border-t-0! md:[&:not(:first-child)]:border-l">
      <div className="type-label text-subtle">{label}</div>
      <div className={cn('mt-0.5 type-metric', tone ?? 'text-content')}>{value}</div>
      {hint && <div className="mt-0.5 type-caption text-subtle">{hint}</div>}
    </div>
  );
}

export function RetailerDetailPage() {
  const { token } = theme.useToken();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const canEdit = usePermission('RETAILER.EDIT');
  const canTransfer = usePermission('TERRITORY.TRANSFER');
  const canVerifyKyc = usePermission('RETAILER.KYC.VERIFY');
  // Gates only the LINK to the scheme register — the enrolment itself is part of this dealer's
  // commercial terms and belongs on the page for anyone who can read the dealer.
  const canViewSchemes = usePermission('COMMERCIAL.VIEW');
  const qc = useQueryClient();
  const [rejectKycOpen, setRejectKycOpen] = useState(false);
  const [changeTerrOpen, setChangeTerrOpen] = useState(false);
  const [tab, setTab] = useUrlTab(
    'tab',
    ['overview', 'orders', 'invoices', 'ledger', 'visits', 'complaints', 'agreement'] as const,
    'overview',
  );

  const { data: retailer, isLoading, isError } = useQuery({
    queryKey: ['retailer', id],
    queryFn: () => request<any>({ url: `/retailers/${id}` }),
  });

  /**
   * Editing happens in place, one field at a time.
   *
   * Which fields carry a pencil is decided by the server (`editableFields` on the record), not by
   * reading role names here: a user can hold several roles, the union is what counts, and a hidden
   * control is a hint — the PATCH route enforces the same table regardless.
   */
  const editableFields = new Set<string>(((retailer as any)?.editableFields ?? []) as string[]);
  // Money terms are Admin-only; KYC evidence re-opens verification when it changes. Both are
  // enforced server-side — these two sets exist only to EXPLAIN the lock or warn before saving.
  const CREDIT_FIELDS = new Set(['creditLimit', 'creditPeriodDays', 'discountType', 'generalDiscountPct']);
  const IDENTITY_FIELDS = new Set(['gstNumber', 'panNumber', 'agriLicenseNo', 'photoUrl', 'gstCertUrl', 'panCardUrl', 'agriLicenseUrl']);
  const kycOnFile = !!(retailer as any)?.kycVerifiedAt || retailer?.status === 'ACTIVE';

  const saveField = async (patch: Record<string, any>) => {
    await request({ url: `/retailers/${id}`, method: 'PATCH', data: patch });
    await qc.invalidateQueries({ queryKey: ['retailer', id] });
    message.success('Saved');
  };

  const fieldProps = (field: string) => ({
    editable: editableFields.has(field),
    lockReason: editableFields.has(field)
      ? undefined
      : CREDIT_FIELDS.has(field)
        ? 'Only an Admin can change credit and discount terms.'
        : 'You do not have permission to edit this dealer.',
    confirm: IDENTITY_FIELDS.has(field) && kycOnFile
      ? 'This is KYC evidence. Changing it clears the verification and the dealer returns to KYC pending.'
      : undefined,
    onSave: (v: any) => saveField({ [field]: v }),
  });

  const { data: ledger } = useQuery({
    queryKey: ['retailer-ledger', id],
    queryFn: () => request<any[]>({ url: `/retailers/${id}/ledger` }),
    enabled: !!id,
  });
  // "Wallet" summary: spendable (free) credit vs cash committed to un-invoiced orders vs owed — so

  /* ── The Ledger tab's own filters ─────────────────────────────────────────────────────────────
     A statement is read in slices — "what moved in August", "just the payments" — and the tab used to
     offer neither, so the only way to answer either question was to export everything and filter it
     somewhere else. URL-backed, so a filtered statement is a link somebody can be sent.

     The narrowing is applied here rather than server-side because the tab already holds the whole
     ledger; the SAME rule (IST days, inclusive both ends) is applied server-side to the export by
     `applyLedgerFilters`, so the file and the screen select the same rows. */
  const [ledgerTypes, setLedgerTypes] = useUrlList('ltype');
  const [ledgerRange, setLedgerRange] = useUrlRangeOrNull('lperiod');
  const [ledgerExportOpen, setLedgerExportOpen] = useState(false);
  const ledgerFiltered = ledgerTypes.length > 0 || !!ledgerRange;

  const inLedgerPeriod = (e: any) => {
    if (!ledgerRange) return true;
    const d = toIstDateString(e.createdAt);
    return d >= ledgerRange[0].format('YYYY-MM-DD') && d <= ledgerRange[1].format('YYYY-MM-DD');
  };
  const ledgerRows = useMemo(() => {
    const rows: any[] = ledger ?? [];
    if (!ledgerFiltered) return rows;
    const want = ledgerTypes.length ? new Set(ledgerTypes) : null;
    return rows.filter((e) => (!want || want.has(String(e.txnType))) && inLedgerPeriod(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledger, ledgerTypes, ledgerRange, ledgerFiltered]);

  /**
   * The type options, each carrying the count it would leave on screen.
   *
   * Counted against the rows the PERIOD has already selected, so the number is what picking that type
   * actually gives you and not a promise the date filter then breaks. A type currently chosen stays on
   * the list even at zero — a filter you cannot see is a filter you cannot clear.
   */
  const ledgerTypeOptions = useMemo(() => {
    const rows: any[] = ledger ?? [];
    const counts = new Map<string, number>();
    for (const e of rows.filter(inLedgerPeriod)) {
      const t = String(e.txnType);
      counts.set(t, (counts.get(t) ?? 0) + 1);
    }
    const present = [...new Set([...rows.map((e) => String(e.txnType)), ...ledgerTypes])];
    return present
      .map((t) => ({ t, label: ledgerTypeLabel(t), tone: LEDGER_TYPE_META[t]?.tone ?? 'neutral', n: counts.get(t) ?? 0 }))
      .sort((a, b) => b.n - a.n || a.label.localeCompare(b.label))
      .map(({ t, label, tone, n }) => ({
        value: t,
        title: label,
        searchLabel: label,
        selectedText: label,
        chipHue: TAG_TONE_HUE[tone],
        label: (
          <span className="flex w-full items-center gap-2">
            <Tag tone={tone as TagTone} style={{ margin: 0 }}>{label}</Tag>
            <span className="ml-auto type-caption tabular-nums text-subtle">{n}</span>
          </span>
        ),
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledger, ledgerTypes, ledgerRange]);

  // Informational credit-application lines carry no debit, credit or balance, so the statement — in
  // either format — leaves them out. Said out loud in the dialog, but only when there are any.
  const ledgerMemoCount = ledgerRows.filter((e: any) => e.isMemo).length;

  // Statement-of-account download — Admin / Accounts / Super-Admin only.
  const canExportLedger = usePermission('RETAILER.LEDGER.EXPORT');
  // Gate the cross-module tab queries so a viewer without that module's permission (e.g. Accounts
  // lacks COMPLAINT/VISIT) doesn't fire background 403s. Full-permission users are unaffected.
  const canViewOrders = usePermission('ORDER.VIEW');
  const canViewInvoices = usePermission('INVOICE.VIEW');
  const canViewComplaints = usePermission('COMPLAINT.VIEW');
  const canViewVisits = usePermission('VISIT.VIEW');
  const { data: orders } = useQuery({
    queryKey: ['retailer-orders', id],
    // `includeClosed`: `/orders` hides CLOSED unless asked, so the dealer's Orders tab and its count left out every
    // finished order — Shree Ganesh Krishi Kendra read "Orders (3)" with four on record (E2E 15 Sep).
    queryFn: () => request<any>({ url: '/orders', params: { retailerId: id, pageSize: 50, includeClosed: 'true' } }),
    enabled: !!id && canViewOrders,
  });

  const { data: invoices } = useQuery({
    queryKey: ['retailer-invoices', id],
    queryFn: () => request<any[]>({ url: '/invoices', params: { retailerId: id } }),
    enabled: !!id && canViewInvoices,
  });

  const { data: complaints } = useQuery({
    queryKey: ['retailer-complaints', id],
    queryFn: () => request<any[]>({ url: '/complaints', params: { retailerId: id } }),
    enabled: !!id && canViewComplaints,
  });

  const { data: visits } = useQuery({
    queryKey: ['retailer-visits', id],
    queryFn: () => request<any[]>({ url: '/visits', params: { retailerId: id } }),
    enabled: !!id && canViewVisits,
  });

  const { data: agreements } = useQuery({
    queryKey: ['retailer-agreements', id],
    queryFn: () => request<any[]>({ url: `/retailers/${id}/agreements` }),
    enabled: !!id,
  });
  const [renewOpen, setRenewOpen] = useState(false);

  // KYC transitions. All three re-read the retailer, so the panel, the status chip in the header and
  // the approvals badge move together rather than drifting until a manual refresh.
  const afterKyc = (msg: string) => {
    message.success(msg);
    qc.invalidateQueries({ queryKey: ['retailer', id] });
    qc.invalidateQueries({ queryKey: ['retailers'] });
    qc.invalidateQueries({ queryKey: ['approvals'] });
  };
  const kycError = (e: any) => message.error(e?.response?.data?.error?.message ?? 'Failed');
  const submitKyc = useConfirmedMutation({ title: "Submit this retailer’s KYC?", content: "The documents go for verification and can’t be edited while they are under review.", okText: "Yes, submit" }, {
    mutationFn: () => request({ url: `/retailers/${id}/kyc/submit`, method: 'POST' }),
    onSuccess: () => afterKyc('Submitted for KYC verification'), onError: kycError,
  });
  const verifyKyc = useConfirmedMutation({ title: "Verify this retailer’s KYC?", content: "The retailer becomes active and can be ordered for. Changing the documents later un-verifies them again.", okText: "Yes, verify" }, {
    mutationFn: () => request({ url: `/retailers/${id}/kyc/verify`, method: 'POST' }),
    onSuccess: () => afterKyc('KYC verified — retailer activated'), onError: kycError,
  });
  const rejectKyc = useConfirmedMutation({ title: "Reject this retailer’s KYC?", content: "The retailer stays inactive. Your reason goes back to whoever submitted it.", okText: "Yes, reject", danger: true }, {
    mutationFn: (reason: string) => request({ url: `/retailers/${id}/kyc/reject`, method: 'POST', data: { reason } }),
    onSuccess: () => { setRejectKycOpen(false); afterKyc('Sent back to the rep'); },
    onError: kycError,
  });

  // Resolve territory + assigned-sales-person ids → names (gated; fall back to a
  // row name field or #id when the viewer lacks the master-data permission).
  const canViewTerritories = usePermission('TERRITORY.VIEW');
  const canViewSalesUsers = usePermission('USER.VIEW');
  const { data: territories } = useQuery({
    queryKey: ['retailer-territories'],
    queryFn: () => request<any[]>({ url: '/territory-assignments/me/territories' }),
    enabled: !!id && canViewTerritories,
  });
  /**
   * Reps who actually WORK this dealer's territory (bound to it or an ancestor), each tagged with
   * the area that puts them in scope.
   *
   * The picker used to list all 500 users by bare name, which was unusable — several near-identical
   * QA names with nothing to tell them apart — and worse, most of them would have been REJECTED:
   * the server refuses an owner who does not cover the territory (`SP_NOT_IN_TERRITORY`). Reading
   * the same endpoint the validation uses means the list offers exactly what will be accepted.
   */
  const { data: territorySps } = useQuery({
    queryKey: ['retailer-territory-sps', retailer?.territoryId],
    queryFn: () => request<any>({ url: '/territory-assignments/sales-persons', params: { territoryId: retailer?.territoryId } }),
    // Only the OWNER picker (RETAILER.EDIT) uses this list, and the endpoint needs territory/retailer
    // write permissions — so Accounts opening a dealer page 403'd on every visit for a list it would
    // never be shown.
    enabled: !!retailer?.territoryId && canEdit,
  });

  const { data: spUsers } = useQuery({
    queryKey: ['retailer-sp-users'],
    queryFn: () => requestAllPages('/users'),
    enabled: !!id && canViewSalesUsers,
  });

  if (isLoading) return <div className="flex items-center justify-center gap-2 py-10 text-muted"><Spinner /> Loading…</div>;
  if (isError || !retailer) {
    return (
      <Result
        status="error"
        title="Couldn't load this retailer"
        subTitle="It may have been removed, or you may not have access."
        extra={<Button variant="primary" onClick={() => navigate('/people?tab=retailers')}>Back to retailers</Button>}
      />
    );
  }

  // Credit summary — kept simple and intuitive.
  //   outstanding > 0  → they OWE you that much (due)
  //   outstanding < 0  → they're IN CREDIT (paid in advance)
  //   available        = credit limit − outstanding (how much they can still order)
  const limit = Number(retailer.creditLimit) || 0;
  // ONE authoritative passbook balance (order-as-obligation model): outstanding_balance is the single running
  // ledger balance. DB positive = owes (Dr); DB negative = reusable account credit (Cr). userBalance B = −out.
  // Every placed order already posted its obligation debit, so the raw balance is the truth — no committed-prepay
  // or free-credit split (that was the old symptom-patch, removed per the passbook spec §10/§11).
  const out = Number(retailer.outstandingBalance) || 0;
  const owed = Math.max(0, out);        // outstanding due = max(0, −B)
  const advance = Math.max(0, -out);    // reusable account credit = max(0, B)
  const available = limit - out;        // availableToOrder = creditLimit + B
  const utilPct = limit > 0 ? Math.max(0, Math.min(100, Math.round((owed / limit) * 100))) : 0;
  const utilColor = utilPct >= 90 ? PALETTE.danger[500] : utilPct >= 60 ? PALETTE.harvest[400] : PALETTE.success[500];
  const lat = Number(retailer.latitude);
  const lng = Number(retailer.longitude);
  const territoryLabel = (() => {
    const t = (territories ?? []).find((x: any) => String(x.id) === String(retailer.territoryId));
    if (t) return `${t.code} · ${t.name}`;
    // The detail response names it too, so a viewer without TERRITORY.VIEW still gets a label.
    if (retailer.territoryName) return retailer.territoryCode ? `${retailer.territoryCode} · ${retailer.territoryName}` : retailer.territoryName;
    return retailer.territoryId ? `#${retailer.territoryId}` : '—';
  })();
  // Pickers for the two assignment fields. Both are scoped lists the page already loads — the
  // territory list is the actor's own, so a rep cannot reassign a dealer outside their patch (the
  // server re-checks this on PATCH regardless).
  // Code chip + place name, never "CODE · Name" — one shared builder for every territory picker.
  const territoryOptions = (territories ?? []).map((t: any) => ({ value: String(t.id), ...territoryOption(t) }));
  // Coverage-aware options. Falls back to the plain user list only when NOBODY works the territory —
  // which is exactly the case where the server also allows any rep, so the two stay in step.
  const coverageSps = (territorySps?.salesPersons ?? []) as any[];
  const defaultOwnerId = territorySps?.defaultOwnerId != null ? String(territorySps.defaultOwnerId) : null;
  const spOptions = coverageSps.length
    ? coverageSps.map((u: any) => ({
        value: String(u.id),
        label: u.fullName ?? `#${u.id}`,
        coverage: u.territory,
        level: u.territoryLevel,
        primary: String(u.id) === defaultOwnerId,
      }))
    : ((spUsers?.data ?? []) as any[]).map((u: any) => ({ value: String(u.id), label: u.fullName ?? `#${u.id}`, coverage: null, primary: false }));

  /** Name on top, the area that puts them in scope underneath — the reason this rep is offered. */
  const renderSpOption = (o: any) => (
    <span className="flex w-full min-w-0 flex-col gap-0.5 py-0.5">
      <span className="flex items-center gap-1.5">
        <span className="truncate">{o.label}</span>
        {o.primary && <Tag tone="green">primary</Tag>}
      </span>
      {o.coverage && <span className="truncate type-caption text-subtle">{o.coverage}{o.level ? ` · ${String(o.level).toLowerCase()}` : ''}</span>}
    </span>
  );
  const spLabel = (() => {
    if (!retailer.assignedSalesPersonId) return undefined;
    const u = (spUsers?.data ?? []).find((x: any) => String(x.id) === String(retailer.assignedSalesPersonId));
    const name = u?.fullName ?? retailer.assignedSalesPersonName ?? `#${retailer.assignedSalesPersonId}`;
    const cov = coverageSps.find((x: any) => String(x.id) === String(retailer.assignedSalesPersonId))?.territory;
    return (
      <span className="flex flex-col">
        <span>{name}</span>
        {cov && <span className="type-caption text-subtle">{cov}</span>}
      </span>
    );
  })();
  // Dealer agreement: the full renewal trail lives on its own tab — the profile carries the current
  // one so the whole edit form is readable without opening it.
  const agreementState = agreementTone(retailer.agreementExpiry);
  // The server computes the checklist (it owns the rule and enforces it); this is only what to show.
  const kycMissing: string[] = retailer.kycMissingDocuments ?? [];
  const gstin = String(retailer.gstNumber ?? '').trim().toUpperCase();
  const gstinPan = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]{3}$/.test(gstin) ? gstin.slice(2, 12) : null;
  const kycState: 'VERIFIED' | 'SUBMITTED' | 'SENT_BACK' | 'NOT_SUBMITTED' =
    retailer.kycVerifiedAt ? 'VERIFIED'
      : retailer.kycSubmittedAt ? 'SUBMITTED'
        : retailer.kycRejectedReason ? 'SENT_BACK' : 'NOT_SUBMITTED';
  const waNumber = (retailer.whatsappNumber ?? retailer.mobile ?? '').replace('+', '');

  return (
    <div>
      {/* Hero — identity and the money band in ONE card. They were two stacked cards with a 16px
          gap and two sets of padding between them, which bought nothing: the money IS this retailer's
          headline, so it belongs under the name rather than in a separate slab. */}
      <Card className="mb-4" bodyClassName="p-0">
        {/* Wraps on narrow screens: the actions Col is `flex="none"` (never shrinks), so with the row
            pinned to one line the whole hero — and the page — ran off the right of a phone. */}
        <Row align="middle" gutter={16} className="p-4">
          <Col flex="none">
            {/* The shop photo IS this retailer's face — it was uploaded, stored and listed in the
                documents tray, but the hero still drew an empty green disc. Falls back to the store
                glyph when no photo is on file. */}
            <Avatar
              size={56}
              src={retailer.photoUrl || undefined}
              style={{ background: 'var(--color-brand-600)', fontSize: 22, fontWeight: 600 }}
              fallback={<Store size={24} />}
            />
          </Col>
          <Col flex="auto" style={{ minWidth: 0 }}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              {retailer.companyName}
            </Typography.Title>
            <Space size={8} wrap style={{ marginTop: 4 }}>
              <CopyValue value={retailer.code} label="Retailer code"><Tag>{retailer.code}</Tag></CopyValue>
              <Tag tone={STATUS_COLOR[retailer.status] ?? 'neutral'}>{retailerStatusLabel(retailer.status)}</Tag>
              <span style={{ color: token.colorTextSecondary }}>
                {[retailer.contactPerson, [retailer.city, stateName(retailer.stateCode) || null].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
              </span>
            </Space>
          </Col>
          <Col flex="none">
            <Space wrap size={6}>
              <Tooltip label="Call">
                <Button size="sm" href={`tel:${displayMobile(retailer.mobile)}`} icon={<Phone size={14} />} aria-label="Call" />
              </Tooltip>
              <Tooltip label="WhatsApp">
                <Button size="sm" href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener" icon={<MessageCircle size={14} />} aria-label="WhatsApp" />
              </Tooltip>
              {retailer.email && (
                <Tooltip label="Email">
                  <Button size="sm" href={`mailto:${retailer.email}`} icon={<Mail size={14} />} aria-label="Email" />
                </Tooltip>
              )}
              {Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0) && (
                <Tooltip label="View on Google Maps">
                  <Button
                    size="sm"
                    href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
                    target="_blank"
                    rel="noopener"
                    icon={<MapPin size={14} />}
                    aria-label="View on Google Maps"
                  />
                </Tooltip>
              )}
              <Button size="sm" variant="primary" icon={<ShoppingCart size={14} />} onClick={() => navigate(`/orders/new?retailerId=${id}`)}>
                Place Order
              </Button>
              {canTransfer && (
                <Tooltip label="Change territory">
                  <Button size="sm" icon={<ArrowLeftRight size={14} />} aria-label="Change territory" onClick={() => setChangeTerrOpen(true)} />
                </Tooltip>
              )}
            </Space>
          </Col>
        </Row>

        {/* Money band — the whole credit position in ONE strip: limit, the single signed balance
            (credit when B>0, due when B<0), what's left to order, and how much of the limit is used.
            Was four full cards stacked to ~150px; this strip reads left-to-right as one sentence. */}
        <div className="grid grid-cols-2 border-t border-line md:grid-cols-4">
          <MoneyCell label="Credit limit" value={inr2(limit)} hint={`${retailer.creditPeriodDays}-day credit period`} />
          {/* ONE signed net balance, shown as a single mutually-exclusive label (passbook spec §11): credit when
              B>0, due when B<0, settled at 0. No second "prepaid" figure — the balance already nets open orders. */}
          {advance > 0 ? (
            <MoneyCell label="Account credit" value={inr2(advance)} tone="text-success-600 dark:text-success-500" hint="Paid ahead — reusable" />
          ) : (
            <MoneyCell
              label={owed > 0 ? 'Outstanding (due)' : 'Net balance'}
              value={inr2(owed)}
              tone={owed > 0 ? 'text-danger-600 dark:text-danger-500' : 'text-muted'}
              hint={owed > 0 ? 'Owed to you' : 'Settled'}
            />
          )}
          <MoneyCell
            label="Available to order"
            value={inr2(available)}
            tone={available > 0 ? 'text-success-600 dark:text-success-500' : 'text-danger-600 dark:text-danger-500'}
            hint={available > 0 ? 'Headroom left' : 'Over the limit'}
          />
          <MoneyCell
            label="Credit used"
            value={<span className="flex items-baseline gap-2">{utilPct}%<span className="type-caption text-subtle">of {inr2(limit)}</span></span>}
            hint={<Progress percent={utilPct} strokeColor={utilColor} showInfo={false} />}
          />
        </div>
      </Card>

      {/* Tabs — the open tab lives in the URL so opening an order/invoice from here and pressing Back
          returns to that tab, not to Overview. */}
      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: 'overview',
            label: 'Overview',
            children: (
              /* Two columns that actually BALANCE: the profile no longer runs ~1000px down the left
                 while a 320px map leaves the right half empty. Address moved in with the map (they
                 answer the same question), documents became a full-width tray under both, and every
                 field grid is 3- or 4-up instead of 2-up — the same information in about half the height. */
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
                <div className="lg:col-span-8">
                  <Card title="Profile" className="h-full">
                    <div className="space-y-5">
                      {/* Contact */}
                      <section>
                        <SectionH icon={<User className="size-3.5" />} tint="bg-brand-600/10 text-brand-700 dark:bg-brand-400/10 dark:text-brand-400">Contact</SectionH>
                        <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-3">
                          <InlineEditField label="Contact person" value={retailer.contactPerson} {...fieldProps('contactPerson')}>{retailer.contactPerson}</InlineEditField>
                          <InlineEditField label="Mobile (WhatsApp)" value={retailer.mobile} type="phone" {...fieldProps('mobile')}>
                            {retailer.mobile ? (
                              <span className="inline-flex items-center gap-2">
                                <CopyValue value={retailer.mobile} label="Mobile"><span>{displayMobile(retailer.mobile)}</span></CopyValue>
                                <a href={`tel:${retailer.mobile}`} aria-label="Call" className="flex size-6 items-center justify-center rounded-full bg-brand-600/10 text-brand-700 transition hover:bg-brand-600/20 dark:text-brand-400"><Phone className="size-3" /></a>
                                {waNumber && <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener" aria-label="WhatsApp" className="flex size-6 items-center justify-center rounded-full bg-green-500/10 text-green-600 transition hover:bg-green-500/20 dark:text-green-400"><MessageCircle className="size-3" /></a>}
                              </span>
                            ) : undefined}
                          </InlineEditField>
                          <InlineEditField label="Email" value={retailer.email} {...fieldProps('email')}>
                            {retailer.email ? <a href={`mailto:${retailer.email}`} className="break-all text-brand-700 hover:underline dark:text-brand-400">{retailer.email}</a> : undefined}
                          </InlineEditField>
                        </dl>
                      </section>

                      {/* Tax & compliance */}
                      <section>
                        <SectionH icon={<FileText className="size-3.5" />} tint="bg-blue-500/10 text-blue-600 dark:bg-blue-400/10 dark:text-blue-400">Tax &amp; compliance</SectionH>
                        <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-3">
                          <InlineEditField label="GSTIN" value={retailer.gstNumber} {...fieldProps('gstNumber')}>
                            {retailer.gstNumber
                              ? <CopyValue value={retailer.gstNumber} label="GSTIN"><Mono>{retailer.gstNumber}</Mono></CopyValue>
                              // Amber, not red: worth collecting, but not a KYC requirement — an unregistered or
                              // composition dealer has no GSTIN at all. Red "Missing" is kept for what blocks KYC.
                              : <Tooltip label="Optional — not required for KYC. Small unregistered dealers have no GSTIN."><span><Tag tone="gold">Not recorded</Tag></span></Tooltip>}
                          </InlineEditField>
                          <InlineEditField label="PAN" value={retailer.panNumber} {...fieldProps('panNumber')}>
                            {retailer.panNumber
                              ? <CopyValue value={retailer.panNumber} label="PAN"><Mono>{retailer.panNumber}</Mono></CopyValue>
                              // Required from every dealer. When a GSTIN is on file, characters 3–12 of it are the
                              // PAN — shown as a hint so whoever fills it in can copy it rather than chase it.
                              : <span className="inline-flex flex-wrap items-center gap-2">
                                  <Tag tone="red">Missing</Tag>
                                  {gstinPan && <span className="type-caption font-normal text-subtle">GSTIN shows <Mono>{gstinPan}</Mono></span>}
                                </span>}
                          </InlineEditField>
                          <InlineEditField label="Agri license no." value={retailer.agriLicenseNo} {...fieldProps('agriLicenseNo')}>{retailer.agriLicenseNo ? <CopyValue value={retailer.agriLicenseNo} label="Agri licence no." /> : <Tag tone="red">Missing</Tag>}</InlineEditField>
                        </dl>
                      </section>

                      {/* Commercial & assignment */}
                      <section>
                        <SectionH icon={<Percent className="size-3.5" />} tint="bg-amber-500/10 text-amber-600 dark:bg-amber-400/10 dark:text-amber-400">Commercial &amp; assignment</SectionH>
                        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
                          {/* The money TERMS live here, beside the assignment they were agreed with.
                              The band at the top of the page stays a read-only summary — a headline
                              figure is for reading at a glance, not for typing into. */}
                          <InlineEditField label="Discount" value={retailer.discountType ?? 'CUSTOM'} type="select"
                            options={[{ value: 'CUSTOM', label: 'Customize (per order)' }, { value: 'GENERAL', label: 'General (fixed %)' }]}
                            {...fieldProps('discountType')}>
                            {retailer.discountType === 'GENERAL'
                              ? <Tag tone="blue">General · {Number(retailer.generalDiscountPct ?? 0)}%</Tag>
                              : <Tag>Per order</Tag>}
                          </InlineEditField>
                          {retailer.discountType === 'GENERAL' && (
                            <InlineEditField label="Fixed discount %" value={retailer.generalDiscountPct} type="number"
                              {...fieldProps('generalDiscountPct')}>
                              {retailer.generalDiscountPct != null ? `${Number(retailer.generalDiscountPct)}%` : undefined}
                            </InlineEditField>
                          )}
                          <InlineEditField label="Credit limit" value={retailer.creditLimit} type="number"
                            {...fieldProps('creditLimit')}>
                            {retailer.creditLimit != null ? inr2(Number(retailer.creditLimit)) : undefined}
                          </InlineEditField>
                          <InlineEditField label="Credit period" value={retailer.creditPeriodDays} type="number"
                            {...fieldProps('creditPeriodDays')}>
                            {retailer.creditPeriodDays != null ? `${Number(retailer.creditPeriodDays)} days` : undefined}
                          </InlineEditField>
                          {/* The whole address chain, not just the leaf: "Pune" alone does not say
                              which state or which beat, and this is the page people open to answer
                              exactly that. `fallbackName` keeps a viewer without TERRITORY.VIEW
                              reading a name rather than a gap. */}
                          <InlineEditField label="Territory" value={retailer.territoryId} type="select" options={territoryOptions} {...fieldProps('territoryId')}>
                            {/* `wrap`: in the two-column phone grid the unwrapped chip path pushed the page 23px wider than a 360px screen. */}
                            <TerritoryChips territoryId={retailer.territoryId} fallbackName={retailer.territoryName} wrap />
                          </InlineEditField>
                          <InlineEditField label="Assigned SP" value={retailer.assignedSalesPersonId} type="select" options={spOptions}
                            optionRender={renderSpOption} {...fieldProps('assignedSalesPersonId')}>{spLabel}</InlineEditField>
                          <OField label="Onboarded">{formatIstDate(retailer.onboardedAt)}</OField>
                          {/* Who added this dealer, when, and in which of their shifts — the field work behind the account. */}
                          <OField label="Registered by"><RegisteredBy r={retailer} /></OField>
                          {/*
                            * The commercial SCHEMES this dealer signed. "Discount: Per order" above
                            * says only how a discount is decided, not what terms the dealer is on —
                            * the credit period, the advance/timeline CD slabs and the turnover bonus
                            * all live in a scheme, and this page never mentioned them.
                            */}
                          <OField label="Schemes" className="col-span-2 sm:col-span-4">
                            <SchemeEnrolments schemes={retailer.schemes} canView={canViewSchemes} />
                          </OField>
                        </dl>
                      </section>

                      {/* App access — the login row + device binding are already on the detail response
                          and were shown nowhere, so "can this dealer actually use the app?" and "is a
                          handset still bound to them?" could not be answered from this page at all. */}
                      <section>
                        <SectionH icon={<Smartphone className="size-3.5" />} tint="bg-violet-500/10 text-violet-600 dark:bg-violet-400/10 dark:text-violet-400">App access</SectionH>
                        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
                          <OField label="Login">
                            {retailer.appUserId
                              ? <Tag tone={retailer.loginStatus === 'active' ? 'green' : 'gold'}>{String(retailer.loginStatus ?? 'unknown')}</Tag>
                              : <Tag>Never logged in</Tag>}
                          </OField>
                          <OField label="Device">
                            {retailer.deviceBound
                              ? <span className="inline-flex items-center gap-1.5"><Tag tone="green">Bound</Tag>{retailer.deviceSuffix ? <Mono>…{retailer.deviceSuffix}</Mono> : null}</span>
                              : <Tag>Not bound</Tag>}
                          </OField>
                          <OField label="Account status"><Tag tone={STATUS_COLOR[retailer.status] ?? 'neutral'}>{retailerStatusLabel(retailer.status)}</Tag></OField>
                          <OField label="Retailer code"><CopyValue value={retailer.code} label="Retailer code"><Mono>{retailer.code}</Mono></CopyValue></OField>
                        </dl>
                      </section>
                    </div>
                  </Card>
                </div>

                {/* Location — the map and the address it pins, in one card instead of two places. */}
                <div className="lg:col-span-4">
                  <Card title="Location" bodyClassName="p-0" className="h-full">
                    {Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0) ? (
                      <MapContainer center={[lat, lng]} zoom={13} style={{ height: 236, width: '100%' }} scrollWheelZoom={false}>
                        <InvalidateSize />
                        <TileLayer attribution={MAP_TILE_ATTRIBUTION} url={MAP_TILE_URL} />
                        <Marker position={[lat, lng]} icon={retailerPin}>
                          <Popup>{retailer.companyName}</Popup>
                        </Marker>
                      </MapContainer>
                    ) : (
                      <div className="flex h-[236px] flex-col items-center justify-center gap-1.5 bg-surface-2">
                        <MapPin className="size-5 text-subtle" />
                        <span className="type-caption text-subtle">No GPS coordinates on record</span>
                      </div>
                    )}
                    {/*
                      * THE ADDRESS IS EDITABLE HERE, like everything else on this page.
                      *
                      * It was the one block still printed as a sentence — "Shop 4, Ganesh Complex,
                      * Alephata Road, Narayangaon, 27 – 410504" — with no way to correct it short of
                      * the create form, even though the server has always accepted addressLine1 /
                      * addressLine2 / city / stateCode / pincode under the ordinary EDIT gate. Split
                      * into the fields the record actually stores, each carrying its own pencil and
                      * its own permission, so a wrong door number is a two-click fix.
                      *
                      * State stays a PICKER, never free text: the code is the dealer's place of
                      * supply and decides CGST+SGST vs IGST on every invoice — a typed "MH" would
                      * silently break tax determination.
                      */}
                    <dl className="grid grid-cols-6 gap-x-4 gap-y-3 border-t border-line/70 p-4">
                      <InlineEditField className="col-span-6" label="Address line 1"
                        value={retailer.addressLine1} {...fieldProps('addressLine1')}>
                        {retailer.addressLine1 || undefined}
                      </InlineEditField>
                      <InlineEditField className="col-span-6" label="Address line 2"
                        value={retailer.addressLine2} {...fieldProps('addressLine2')}>
                        {retailer.addressLine2 || undefined}
                      </InlineEditField>
                      <InlineEditField className="col-span-3 sm:col-span-2" label="City"
                        value={retailer.city} {...fieldProps('city')}>
                        {retailer.city || undefined}
                      </InlineEditField>
                      <InlineEditField className="col-span-3 sm:col-span-2" label="State" type="select" options={STATE_OPTIONS}
                        value={retailer.stateCode} {...fieldProps('stateCode')}>
                        {retailer.stateCode ? `${retailer.stateCode} · ${stateName(retailer.stateCode)}` : undefined}
                      </InlineEditField>
                      <InlineEditField className="col-span-3 sm:col-span-2" label="PIN code"
                        value={retailer.pincode} {...fieldProps('pincode')}>
                        {retailer.pincode || undefined}
                      </InlineEditField>
                      <OField className="col-span-6" label="Coordinates">
                        {Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0) ? (
                          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1.5">
                            <CopyValue value={`${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`} label="Coordinates"><Mono>{Number(lat).toFixed(6)}, {Number(lng).toFixed(6)}</Mono></CopyValue>
                            {/* An action, not prose — same brand chip the other map links wear, so it reads as a button next to the coordinate chip instead of stray text. */}
                            <a href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`} target="_blank" rel="noopener" className="inline-flex items-center gap-1.5 rounded-md bg-brand-50 px-2 py-1 type-label-strong leading-none text-brand-700! no-underline transition-colors duration-150 hover:bg-brand-100 dark:bg-brand-900/30 dark:text-brand-300! dark:hover:bg-brand-900/50">
                              <ExternalLink className="size-3.5" /> Open in Maps
                            </a>
                          </span>
                        ) : undefined}
                      </OField>
                    </dl>
                  </Card>
                </div>

                {/* Documents & agreement — a full-width tray. Six slots side by side is one short row;
                    3-up inside the profile column it was two tall ones. */}
                <div className="lg:col-span-12">
                  {/* KYC lives in the header of the very card that holds the documents it is about —
                      the verdict, who reached it, and the actions, next to the evidence. */}
                  <Card
                    title="Documents & agreement"
                    density="compact"
                    extra={
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {kycState === 'VERIFIED' && (
                          <span className="flex items-center gap-2">
                            <Tag tone="green"><ShieldCheck className="mr-1 inline size-3" />KYC verified</Tag>
                            <span className="type-caption text-subtle">
                              {retailer.kycVerifiedByName ? `by ${retailer.kycVerifiedByName} · ` : 'grandfathered · '}
                              {formatIstDate(retailer.kycVerifiedAt)}
                            </span>
                          </span>
                        )}
                        {kycState === 'SUBMITTED' && (
                          <span className="flex items-center gap-2">
                            <Tag tone="gold">Awaiting KYC verification</Tag>
                            <span className="type-caption text-subtle">
                              {retailer.kycSubmittedByName ? `by ${retailer.kycSubmittedByName} · ` : ''}
                              {formatIstDate(retailer.kycSubmittedAt)}
                            </span>
                          </span>
                        )}
                        {kycState === 'SENT_BACK' && <Tag tone="red">KYC sent back</Tag>}
                        {kycState === 'NOT_SUBMITTED' && <Tag>KYC not submitted</Tag>}

                        {canVerifyKyc && kycState !== 'VERIFIED' && (
                          <>
                            <Tooltip label={kycMissing.length ? `Missing: ${kycMissing.join(', ')}` : 'Verify the documents and activate this dealer'}>
                              <Button size="sm" variant="primary" icon={<ShieldCheck size={14} />}
                                loading={verifyKyc.isPending} disabled={kycMissing.length > 0}
                                onClick={() => verifyKyc.mutate()}>
                                Verify &amp; activate
                              </Button>
                            </Tooltip>
                            {kycState === 'SUBMITTED' && (
                              <Button size="sm" onClick={() => setRejectKycOpen(true)}>Send back</Button>
                            )}
                          </>
                        )}
                        {canEdit && !canVerifyKyc && kycState !== 'VERIFIED' && kycState !== 'SUBMITTED' && (
                          <Tooltip label={kycMissing.length ? `Missing: ${kycMissing.join(', ')}` : 'Send this file to an Admin for verification'}>
                            <Button size="sm" variant="primary" loading={submitKyc.isPending}
                              disabled={kycMissing.length > 0} onClick={() => submitKyc.mutate()}>
                              Submit for KYC
                            </Button>
                          </Tooltip>
                        )}
                      </div>
                    }
                  >
                    {kycState === 'SENT_BACK' && retailer.kycRejectedReason && (
                      <div className="mb-3 flex items-start gap-2 rounded-control border border-danger-500/30 bg-danger-500/5 px-3 py-2">
                        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-danger-600 dark:text-danger-500" />
                        <span className="type-body text-content"><b>Sent back:</b> {retailer.kycRejectedReason}</span>
                      </div>
                    )}
                    {kycMissing.length > 0 && (
                      <div className="mb-3 flex items-start gap-2 rounded-control border border-amber-500/30 bg-amber-500/5 px-3 py-2">
                        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-500" />
                        <span className="type-body text-content">
                          {kycState === 'VERIFIED'
                            ? <>Verified, but still missing: <b>{kycMissing.join(', ')}</b>.</>
                            : <>KYC incomplete — still needed: <b>{kycMissing.join(', ')}</b>.</>}
                        </span>
                      </div>
                    )}
                    <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4 lg:grid-cols-7">
                      {/* The KYC documents are COLLECTED here, by the rep standing in the shop —
                          so the slot that says a document is missing is the same slot that takes it.
                          Uploading one on a verified dealer re-opens verification (fieldProps warns
                          first); the agreement is not KYC evidence, so it does not. */}
                      <InlineEditField label="Shop photo (optional)" type="file" uploadCategory="retailer-photo"
                        value={retailer.photoUrl} {...fieldProps('photoUrl')}>
                        {retailer.photoUrl ? <DocumentPreview url={retailer.photoUrl} label="Shop photo" /> : undefined}
                      </InlineEditField>
                      <InlineEditField label="GST certificate (optional)" type="file" uploadCategory="attachment"
                        value={retailer.gstCertUrl} {...fieldProps('gstCertUrl')}>
                        {retailer.gstCertUrl ? <DocumentPreview url={retailer.gstCertUrl} label="GST certificate" /> : undefined}
                      </InlineEditField>
                      <InlineEditField label="PAN card (optional)" type="file" uploadCategory="attachment"
                        value={retailer.panCardUrl} {...fieldProps('panCardUrl')}>
                        {retailer.panCardUrl ? <DocumentPreview url={retailer.panCardUrl} label="PAN card" /> : undefined}
                      </InlineEditField>
                      <InlineEditField label="Agri licence copy (required)" type="file" uploadCategory="attachment"
                        value={retailer.agriLicenseUrl} {...fieldProps('agriLicenseUrl')}>
                        {retailer.agriLicenseUrl ? <DocumentPreview url={retailer.agriLicenseUrl} label="Agri licence" /> : undefined}
                      </InlineEditField>
                      <InlineEditField label="Agreement copy" type="file" uploadCategory="attachment"
                        value={retailer.agreementUrl} {...fieldProps('agreementUrl')}>
                        {retailer.agreementUrl ? <DocumentPreview url={retailer.agreementUrl} label="Dealer agreement" /> : undefined}
                      </InlineEditField>
                      <InlineEditField label="Agreement date" type="date"
                        value={retailer.agreementDate} {...fieldProps('agreementDate')}>
                        {retailer.agreementDate ? formatIstDate(retailer.agreementDate) : undefined}
                      </InlineEditField>
                      <InlineEditField label="Valid till" type="date"
                        value={retailer.agreementExpiry} {...fieldProps('agreementExpiry')}>
                        {retailer.agreementExpiry
                          ? <Tag tone={agreementState.color}>{formatIstDate(retailer.agreementExpiry)} · {agreementState.label}</Tag>
                          : undefined}
                      </InlineEditField>
                    </dl>
                  </Card>
                </div>
              </div>
            ),
          },
          {
            key: 'orders',
            label: <span><ShoppingCart size={16} /> Orders ({orders?.meta?.total ?? orders?.data?.length ?? 0})</span>,
            children: (
              <Table
                rowKey="id"
                dataSource={orders?.data ?? []}
                pagination={{ pageSize: 20 }}
                size="small"
                onRow={(o: any) => ({ onClick: () => navigate(`/orders/${o.id}`), style: { cursor: 'pointer' } })}
                columns={[
                  { title: 'Order No', dataIndex: 'orderNo' },
                  { title: 'Date', dataIndex: 'orderDate', render: (v) => formatIst(v) },
                  { title: 'Total', dataIndex: 'grandTotal', render: (v) => inr2(v), align: 'right' as const },
                  { title: 'Status', dataIndex: 'status', render: (s) => <Tag tone={ORDER_COLOR[s]}>{s}</Tag> },
                ]}
              />
            ),
          },
          {
            key: 'invoices',
            label: <span><FileText size={16} /> Invoices ({invoices?.length ?? 0})</span>,
            children: (
              <Table
                rowKey="id"
                dataSource={invoices ?? []}
                pagination={{ pageSize: 20 }}
                size="small"
                onRow={(i) => ({ onClick: () => navigate(`/billing/invoices/${i.id}`), style: { cursor: 'pointer' } })}
                columns={[
                  { title: 'Invoice', dataIndex: 'invoiceNo' },
                  { title: 'Date', dataIndex: 'invoiceDate' },
                  { title: 'Due', dataIndex: 'dueDate' },
                  { title: 'Total', dataIndex: 'grandTotal', render: (v) => inr2(v), align: 'right' as const },
                  { title: 'Paid', dataIndex: 'paidAmt', render: (v) => inr2(v), align: 'right' as const },
                  {
                    title: 'Balance', dataIndex: 'balanceAmt',
                    render: (v) => <b style={{ color: Number(v) > 0 ? PALETTE.danger[500] : PALETTE.success[500] }}>{inr2(v)}</b>,
                    align: 'right' as const,
                  },
                  { title: 'Status', dataIndex: 'status', render: (s) => <Tag tone={INVOICE_COLOR[s]}>{s}</Tag> },
                ]}
              />
            ),
          },
          {
            key: 'ledger',
            label: 'Ledger',
            children: (
              <div>
                {/* ONE export button, not one per format: which file you want is a choice inside the
                    dialog, alongside the period and the entry types it will carry. */}
                <FilterBar
                  className="mb-2"
                  actions={canExportLedger ? (
                    <Button icon={<Download size={16} />} onClick={() => setLedgerExportOpen(true)}>Export</Button>
                  ) : undefined}
                >
                  <Select
                    mode="multiple"
                    allowClear
                    value={ledgerTypes}
                    onChange={(v: any) => setLedgerTypes((v as string[]) ?? [])}
                    options={ledgerTypeOptions}
                    optionFilterProp="searchLabel"
                    optionLabelProp="selectedText"
                    placeholder="All entry types"
                    maxTagCount="responsive"
                    style={{ minWidth: 220 }}
                    aria-label="Entry type"
                  />
                  <DatePicker.RangePicker
                    value={ledgerRange as any}
                    onChange={(v: any) => setLedgerRange(v?.[0] && v?.[1] ? [v[0], v[1]] : null)}
                    disabledDate={disableFuture}
                    allowClear
                    aria-label="Statement period"
                  />
                  {ledgerFiltered && (
                    <FilterResetButton size="md" onClick={() => { setLedgerTypes([]); setLedgerRange(null); }} />
                  )}
                </FilterBar>
                <div className="mb-2 type-caption text-muted">
                  {ledgerFiltered
                    ? `Showing ${ledgerRows.length} of ${(ledger ?? []).length} entries`
                    : `${(ledger ?? []).length} entr${(ledger ?? []).length === 1 ? 'y' : 'ies'}, all time`}
                  {/* A type filter leaves gaps in the run, so say what the Balance column still is —
                      it is the account's balance after that entry, never a total of the rows shown. */}
                  {ledgerTypes.length > 0 && ' · Balance is the account balance after each entry, not a total of these rows.'}
                </div>
                <LedgerStatement data={ledgerRows} />
                {canExportLedger && (
                  /*
                   * ONE LEDGER DOCUMENT IN THE PRODUCT.
                   *
                   * This used to render a statement of its own — a different letterhead, different
                   * columns and a different table from the Retailer Ledger report on the Reports page,
                   * for the very same account. Two documents for one subject is two things to keep
                   * right, and the reader who has seen both has no way to know which is authoritative.
                   * So the button exports THAT report, scoped to this dealer: same engine, same
                   * columns, same colours, and every improvement to reports lands here too.
                   */
                  <ExportDialog
                    open={ledgerExportOpen}
                    onClose={() => setLedgerExportOpen(false)}
                    title="Export statement of account"
                    description={[
                      `The Retailer Ledger report, scoped to ${retailer?.companyName ?? 'this dealer'}${retailer?.code ? ` (${retailer.code})` : ''}.`,
                      'Choose the period, the entries to include, and the format.',
                      ledgerMemoCount ? `${ledgerMemoCount} credit-application note${ledgerMemoCount === 1 ? '' : 's'} shown on screen carry no debit or credit, so the statement leaves them out.` : '',
                    ].filter(Boolean).join(' ')}
                    baseUrl="/reports/RETAILER_LEDGER/export"
                    baseParams={{ retailerId: id }}
                    formats={['pdf', 'xlsx', 'csv']}
                    formatHint="Every entry posted to this account, with the balance after each one."
                    initialRange={ledgerRange}
                    picker={{
                      label: 'Entry types',
                      param: 'txnType',
                      items: ledgerTypeOptions.map((o) => ({ key: String(o.value), name: o.title, tagTone: LEDGER_TYPE_META[String(o.value)]?.tone })),
                      initialSelected: ledgerTypes.length ? ledgerTypes : undefined,
                      emptyText: 'This dealer has no ledger entries yet.',
                    }}
                  />
                )}
              </div>
            ),
          },
          {
            key: 'visits',
            label: <span><CalendarClock size={16} /> Visits ({visits?.length ?? 0})</span>,
            children: (
              <Table
                rowKey="id"
                dataSource={visits ?? []}
                pagination={{ pageSize: 20 }}
                size="small"
                columns={[
                  { title: 'Visit No', dataIndex: 'visitNo' },
                  { title: 'Check-in', dataIndex: 'checkInAt', render: (v) => formatIst(v) },
                  { title: 'Duration', dataIndex: 'durationMin', render: (v) => v ? `${v} min` : '—' },
                  {
                    title: 'Geofence', dataIndex: 'checkInWithinGeofence',
                    render: (v) => v ? <Tag tone="green">In zone</Tag> : <Tag tone="red">Outside</Tag>,
                  },
                  {
                    title: 'Order placed', dataIndex: 'orderPlaced',
                    render: (v) => v ? <Tag tone="green">Yes</Tag> : <Tag>No</Tag>,
                  },
                  // The shared visit quality score (the one the visit's own dialog shows), not the legacy column.
                  { title: 'Quality', dataIndex: 'quality', render: (q: any) => (q && Number.isFinite(Number(q.score)) ? `${Math.round(Number(q.score))}%` : '—') },
                ]}
              />
            ),
          },
          {
            key: 'complaints',
            label: <span><Wrench size={16} /> Complaints ({complaints?.length ?? 0})</span>,
            children: (
              <Table
                rowKey="id"
                dataSource={complaints ?? []}
                pagination={{ pageSize: 20 }}
                size="small"
                onRow={(c) => ({ onClick: () => navigate(`/complaints/${c.id}`), style: { cursor: 'pointer' } })}
                columns={[
                  { title: 'No', dataIndex: 'complaintNo' },
                  { title: 'Category', dataIndex: 'category' },
                  { title: 'Severity', dataIndex: 'severity' },
                  { title: 'Status', dataIndex: 'status' },
                  { title: 'Raised', dataIndex: 'createdAt', render: (v) => formatIstDate(v) },
                  {
                    title: 'SLA Due', dataIndex: 'slaDueAt',
                    render: (v) => {
                      if (!v) return '—';
                      const due = new Date(v);
                      const breach = due < new Date();
                      return <span style={{ color: breach ? PALETTE.danger[500] : undefined }}>{formatIst(v)}</span>;
                    },
                  },
                ]}
              />
            ),
          },
          {
            key: 'agreement',
            label: <span><FileLock size={16} /> Agreement ({agreements?.length ?? 0})</span>,
            children: (
              <>
                <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                  <Space size="large" wrap>
                    <Statistic title="Current expiry"
                      formatter={() => {
                        const s = agreementTone(retailer.agreementExpiry);
                        return retailer.agreementExpiry
                          ? <Tag tone={s.color} style={{ fontSize: 14 }}>{formatIstDate(retailer.agreementExpiry)} · {s.label}</Tag>
                          : <Tag>No agreement on file</Tag>;
                      }} />
                    {retailer.agreementUrl && (
                      <Button href={retailer.agreementUrl} target="_blank" rel="noreferrer" icon={<FileText size={16} />}>View current copy</Button>
                    )}
                  </Space>
                  {canEdit && <Button variant="primary" icon={<RefreshCw size={16} />} onClick={() => setRenewOpen(true)}>Renew agreement</Button>}
                </Space>
                <Table
                  rowKey="id"
                  dataSource={agreements ?? []}
                  pagination={{ pageSize: 20 }}
                  size="small"
                  locale={{ emptyText: <EmptyState title="No agreements recorded yet" /> }}
                  columns={[
                    { title: 'Agreement date', dataIndex: 'agreementDate', render: (v) => v ? formatIstDate(v) : '—' },
                    { title: 'Valid till', dataIndex: 'expiryDate', render: (v) => v ? formatIstDate(v) : '—' },
                    { title: 'Notes', dataIndex: 'notes', render: (v) => v ?? '—' },
                    { title: 'Recorded', dataIndex: 'createdAt', render: (v) => formatIst(v) },
                    {
                      title: 'Copy', dataIndex: 'agreementUrl',
                      render: (v) => v ? <a href={v} target="_blank" rel="noreferrer">Open</a> : '—',
                    },
                  ]}
                />
              </>
            ),
          },
        ]}
      />

      {canTransfer && (
        <ChangeTerritoryModal open={changeTerrOpen} onClose={() => setChangeTerrOpen(false)} customerType="RETAILER" customer={retailer} />
      )}
      {canEdit && <RenewAgreementModal open={renewOpen} onClose={() => setRenewOpen(false)} retailerId={id} />}
      {canVerifyKyc && (
        <RejectKycModal
          open={rejectKycOpen}
          onClose={() => setRejectKycOpen(false)}
          pending={rejectKyc.isPending}
          onSubmit={(reason) => rejectKyc.mutate(reason)}
        />
      )}
    </div>
  );
}

// Same red/amber tone logic as the list, for the detail header tag.
function agreementTone(expiry?: string | null): { label: string; color: TagTone } {
  if (!expiry) return { label: 'None', color: 'neutral' };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const exp = new Date(expiry); exp.setHours(0, 0, 0, 0);
  const days = Math.round((exp.getTime() - today.getTime()) / 86_400_000);
  if (days < 0) return { label: `expired ${-days}d ago`, color: 'red' };
  if (days <= 30) return { label: `expires in ${days}d`, color: 'gold' };
  return { label: 'active', color: 'green' };
}

/** Sending a KYC file back REQUIRES a reason — the rep has to know what to re-collect. */
function RejectKycModal({ open, onClose, pending, onSubmit }: {
  open: boolean; onClose: () => void; pending: boolean; onSubmit: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);
  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title="Send this KYC file back"
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={pending} disabled={!reason.trim()} onClick={() => onSubmit(reason.trim())}>
            Send back
          </Button>
        </>
      }
    >
      <p className="mb-3 type-body text-muted">
        The dealer stays as they are — only the submission is cleared, so the rep can fix the documents
        and submit again.
      </p>
      <Input.TextArea
        rows={3}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. PAN card scan is unreadable; agri licence has expired"
      />
    </Modal>
  );
}

function RenewAgreementModal({ open, onClose, retailerId }: { open: boolean; onClose: () => void; retailerId: string }) {
  const qc = useQueryClient();
  const [form] = Form.useForm();

  const save = useConfirmedMutation({ title: "Save this change?", okText: "Yes, save" }, {
    mutationFn: (vals: any) => request({
      url: `/retailers/${retailerId}/agreements`, method: 'POST',
      data: {
        agreementUrl: vals.agreementUrl,
        agreementDate: vals.agreementDate ? vals.agreementDate.format('YYYY-MM-DD') : undefined,
        expiryDate: vals.expiryDate ? vals.expiryDate.format('YYYY-MM-DD') : undefined,
        notes: vals.notes,
      },
    }),
    onSuccess: () => {
      message.success('Agreement renewed');
      qc.invalidateQueries({ queryKey: ['retailer-agreements', retailerId] });
      qc.invalidateQueries({ queryKey: ['retailer', retailerId] });
      qc.invalidateQueries({ queryKey: ['retailers'] });
      form.resetFields();
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to renew'),
  });

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title="Renew dealer agreement"
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>Save agreement</Button></>}>
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)} initialValues={{ agreementDate: dayjs() }}>
        <Form.Item name="agreementUrl" label="Agreement copy" extra="Upload the newly signed agreement (PDF/photo).">
          <FileUploadField category="attachment" buttonLabel="Upload agreement" preview={false} />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="agreementDate" label="Agreement date" rules={[{ required: true, message: 'Pick the agreement date' }]}>
              <DatePicker className="w-full" format="DD MMM YYYY" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="expiryDate" label="Valid till (expiry)" rules={[{ required: true, message: 'Pick the expiry date' }]}>
              <DatePicker className="w-full" format="DD MMM YYYY" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="notes" label="Notes (optional)">
          <Input.TextArea rows={2} placeholder="e.g. 1-year renewal, revised terms…" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
